import { OnboardingStep } from '@repo/shared/dtos/organization'
import { generateId } from '@repo/shared/uuid/uuid'
import { DataProvider } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { ReactNode, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useOutlet, useParams } from 'react-router-dom'

import { RouteNames } from '@/routes'
import { AbilityProvider } from '@/routes/AbilityProvider'
import { AuthChecker } from '@/routes/AuthChecker'
import { LoadingRoute } from '@/routes/LoadingRoute'
import { LocationHandler } from '@/routes/LocationHandler'
import { AvailableParams } from '@/routes/router'
import { RootState } from '@/store'
import { adminStoreActions, storeActions } from '@/store/actions'
import { getCurrentOrganization } from '@/store/helpers'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

export const OrganizationProtectedRoute = () => {
  const outlet = useOutlet()
  return (
    <LocationHandler>
      {({ location, routeName }) => (
        <AuthChecker location={location}>
          <RouteInitializer routeName={routeName}>
            <DataProvider
              loadingComponent={<LoadingRoute />}
              selectors={[
                (state: RootState) => state.user.me,
                getCurrentOrganization,
                (state: RootState) => state.user.caseWorker,
              ]}
              render={(user, _organization, caseWorker) => (
                <AbilityProvider user={user} caseWorker={caseWorker} routeName={routeName}>
                  {outlet}
                </AbilityProvider>
              )}
            />
          </RouteInitializer>
        </AuthChecker>
      )}
    </LocationHandler>
  )
}

function RouteInitializer({ children, routeName }: { children: ReactNode; routeName: RouteNames }) {
  const navigate = useNavigate()
  const { beneficiaryId, organizationId, chatSessionId } = useParams<AvailableParams>()

  const prevRouteNameRef = useRef<RouteNames | null>(null)
  const dispatch = useStoreDispatch()
  const { i18n } = useT()

  const currentBeneficiaryId = useStoreState((state) => state.beneficiary.currentBeneficiaryId)
  const currentOrganizationId = useStoreState((state) => state.user.currentOrganizationId)

  const initActions = useCallback(
    async (routeName: RouteNames) => {
      await dispatch(storeActions.initApp())

      if (organizationId) {
        try {
          const data = await dispatch(
            storeActions.initOrganization({
              organizationId,
              isSameOrganization: currentOrganizationId === organizationId,
            }),
          ).unwrap()

          // Initialize the language of the interface
          if (
            data.caseWorker.language &&
            data.organization.onboarding.step === OnboardingStep.COMPLETED
          )
            void i18n.changeLanguage(data.caseWorker.language)
          else void i18n.changeLanguage(data.organization.language)
        } catch (error) {
          navigate(RouteNames.Root + RouteNames.Login, { replace: true })
          throw new Error(`Error initializing organization: ${JSON.stringify(error)}`)
        }
      } else dispatch(storeActions.cleanUpOrganization())

      if (routeName === RouteNames.Organizations) dispatch(storeActions.initDashboard())

      if (beneficiaryId) {
        await dispatch(
          storeActions.beneficiary.initBeneficiary({
            beneficiaryId,
            isSameBeneficiary: currentBeneficiaryId === beneficiaryId,
          }),
        )
      }

      if (routeName === RouteNames.Beneficiaries) {
        dispatch(
          storeActions.view.beneficiary.profile({
            generationId: generateId(),
          }),
        )
      }

      if (routeName === RouteNames.BeneficiaryTasks) {
        dispatch(
          storeActions.view.beneficiary.tasks({
            generationId: generateId(),
          }),
        )
      }

      if (routeName === RouteNames.BeneficiaryChat) {
        const { chatSessionId } = await dispatch(
          storeActions.beneficiary.chatSession.selectLastChatSession(),
        ).unwrap()
        void dispatch(
          storeActions.beneficiary.chatSession.initChatSession({
            chatSessionId,
          }),
        )
      }

      if (chatSessionId)
        await dispatch(
          storeActions.beneficiary.chatSession.initChatSession({
            chatSessionId,
          }),
        )

      if (routeName === RouteNames.Admin) {
        await dispatch(adminStoreActions.initAdmin())
      }
    },
    [beneficiaryId, chatSessionId, currentBeneficiaryId, currentOrganizationId, organizationId],
  )

  const isDifferentBeneficiary = beneficiaryId && currentBeneficiaryId !== beneficiaryId
  const isDifferentOrganization = organizationId && currentOrganizationId !== organizationId

  useEffect(() => {
    if (
      !prevRouteNameRef.current ||
      prevRouteNameRef.current !== routeName ||
      isDifferentBeneficiary ||
      isDifferentOrganization
    ) {
      prevRouteNameRef.current = routeName
      void initActions(routeName)
    }
  }, [routeName, prevRouteNameRef, initActions, isDifferentBeneficiary, isDifferentOrganization])

  return children
}
